import { useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { POI_FILL } from "../shared/mapModel";
import { MarkerIcon } from "../shared/markers";
import { COUNTRIES, countriesOf, HOSTELS, nightsOf, TRIPS, type HostelKind, type Trip } from "./content";
import GetTripButton from "./GetTripButton";
import { hostelPath, HOME_PATH, mmUrl } from "./links";
import LiveMap from "./LiveMap";
import { hostelMarker, hostelPoint } from "./mapModel";
import { dayRanges, stopsOfTrip } from "./plannerLogic";
import { setRoute } from "./routeStore";
import { scrollToId } from "./scroll";
import TripCard from "./TripCard";
import { Arrow, Flag, MM_MONKEY, StipplMark } from "./ui";
import styles from "./RoutePage.module.scss";
import shared from "./MadMonkey.module.scss";

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(" ");

const KIND_LABEL: Record<HostelKind, string> = { city: "City", town: "Town", island: "Island" };
const KIND_ICON: Record<HostelKind, string> = { city: "city", town: "town", island: "island" };

const dayLabel = ([a, b]: [number, number]) => (a === b ? `Day ${a}` : `Days ${a} to ${b}`);

/** A ready-made route's page, laid out like the stippl.io itinerary blogs. */
const RoutePage = ({ trip }: { trip: Trip }) => {
	const history = useHistory();
	const slugs = useMemo(() => trip.stops.map(([s]) => s), [trip]);
	const stops = useMemo(() => stopsOfTrip(trip), [trip]);
	const ranges = dayRanges(stops);
	const ccs = countriesOf(slugs);
	const fit = useMemo(() => slugs.map(hostelPoint), [slugs]);
	const heroHostels = useMemo(() => slugs.map((s) => hostelMarker(s, { inRoute: true, plate: true })), [slugs]);
	const sideHostels = useMemo(() => slugs.map((s) => hostelMarker(s, { inRoute: true, plate: false })), [slugs]);
	const others = TRIPS.filter((t) => t.id !== trip.id).slice(0, 3);
	const nights = nightsOf(trip);

	const tweak = () => {
		setRoute(stops);
		history.push({ pathname: HOME_PATH, hash: "#hostels" });
	};
	const jumpTo = (id: string) => (e: React.MouseEvent) => {
		e.preventDefault();
		scrollToId(id);
	};

	return (
		<>
			<header className={styles.hero}>
				<div className={styles.s} aria-hidden="true">
					<div className={styles.sv}>
						<img src={HOSTELS[slugs[0]].photos[0]} alt="" />
					</div>
				</div>
				<div className={cx(shared.wrap, styles.heroGrid)}>
					<div className={styles.copy}>
						<nav className={styles.crumb} aria-label="Breadcrumb">
							<Link to={HOME_PATH}>Stippl × Mad Monkey</Link>
							<span>›</span>
							<Link to={{ pathname: HOME_PATH, hash: "#trips" }}>Routes</Link>
							<span>›</span>
							<b>{trip.name}</b>
						</nav>
						<h1>
							{trip.name}: {trip.sub}
						</h1>
						<p className={styles.lede}>{trip.blurb}</p>
						<div className={styles.by}>
							<img src={MM_MONKEY} alt="" />
							<span>
								<b>Stippl × Mad Monkey</b>
								<small>Every night at a Mad Monkey · {ccs.map((c) => COUNTRIES[c]).join(", ")}</small>
							</span>
						</div>
						<div className={styles.stats}>
							<div>
								<MarkerIcon name="star" size={18} />
								<b>{nights}</b>
								<small>nights</small>
							</div>
							<div>
								<MarkerIcon name="bed" size={18} />
								<b>{trip.stops.length}</b>
								<small>Mad Monkeys</small>
							</div>
							<div>
								<span className={styles.flags}>
									{ccs.map((c) => (
										<Flag key={c} cc={c} size={14} />
									))}
								</span>
								<b>{ccs.length}</b>
								<small>countries</small>
							</div>
						</div>
					</div>
					<div className={styles.mapCard}>
						<span className={styles.pill}>
							<Flag cc={ccs[0]} size={16} />
							{trip.name.toUpperCase()} · {nights} NIGHTS
						</span>
						<span className={styles.logo}>
							<StipplMark />
							stippl.
						</span>
						<LiveMap
							still
							id="mm-route-hero"
							label={`Route map: ${trip.name}`}
							hostels={heroHostels}
							route={slugs}
							fit={fit}
							interactive={false}
							dots={false}
						/>
					</div>
				</div>
			</header>

			<div className={cx(shared.wrap, styles.body)}>
				<article className={styles.article}>
					<p className={styles.intro}>{trip.blurb} Every stop below is a Mad Monkey, so each new town starts with The Meet Up and a ready-made crew.</p>
					<h2 className={styles.h2}>{trip.name} at a glance</h2>
					<p>Here is the whole route on one screen: the stops, the nights and how you get from one hostel to the next.</p>
					<div className={styles.table}>
						<table>
							<thead>
								<tr>
									<th>Stop</th>
									<th>Nights</th>
									<th>How you get there</th>
								</tr>
							</thead>
							<tbody>
								{trip.stops.map(([s, n], i) => (
									<tr key={s}>
										<td>{HOSTELS[s].name}</td>
										<td>{n}</td>
										<td>{i ? trip.legs[i - 1] : "Start here"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<p>
						That is {nights} nights across {ccs.length} countr{ccs.length > 1 ? "ies" : "y"}. Got more time? Add the nights where you want to slow down, not
						more stops.
					</p>
					<div className={styles.ai}>
						<span className={styles.aiKicker}>
							<MarkerIcon name="star" size={14} /> Route builder
						</span>
						<h3>Make this route yours in seconds</h3>
						<p>Load it into the Mad Monkey route builder, change the nights, drop a stop or add one, then open it in Stippl with every stop on your map.</p>
						<div className={styles.aiChips}>
							<span>&quot;Two more nights in Pai&quot;</span>
							<span>&quot;Skip Hanoi, add Koh Rong&quot;</span>
							<span>&quot;Start in Bali instead&quot;</span>
						</div>
						<div className={styles.aiBtns}>
							<button type="button" id="mm-route-tweak" className={cx(shared.btn, shared.btnGreen)} onClick={tweak}>
								Tweak it in the route builder
							</button>
							<GetTripButton id="mm-get-trip-route" stops={stops} name={trip.name} source={trip.id} className={cx(shared.btn, styles.ghostOnDark)}>
								Get this trip <Arrow />
							</GetTripButton>
						</div>
					</div>

					{trip.stops.map(([s, n], i) => {
						const h = HOSTELS[s];
						return (
							<section className={styles.stop} id={`stop-${i + 1}`} key={s} aria-labelledby={`stop-${i + 1}-h`}>
								<span className={styles.eyebrow}>{dayLabel(ranges[i])}</span>
								<h2 className={styles.h2} id={`stop-${i + 1}-h`}>
									{h.name}
								</h2>
								<div className={styles.meta}>
									<MarkerIcon name={KIND_ICON[h.kind]} size={15} />
									{KIND_LABEL[h.kind]} <span>·</span> {n} night{n > 1 ? "s" : ""}
								</div>
								<p>
									{h.tag}. {h.about}
								</p>
								{i > 0 && (
									<p>
										<b>Getting there:</b> {trip.legs[i - 1]}.
									</p>
								)}
								<h3 className={styles.h3}>Don&apos;t miss</h3>
								{h.pois ? (
									<ul className={styles.pois}>
										{h.pois.slice(0, 5).map((p) => (
											<li key={p.name}>
												<i style={{ background: POI_FILL[p.grp] }}>
													<MarkerIcon name={p.icon} size={16} />
												</i>
												<span>
													<b>{p.name}</b> {p.why}
												</span>
											</li>
										))}
									</ul>
								) : (
									<ul className={styles.list}>
										{h.todo.map((x) => (
											<li key={x}>{x}</li>
										))}
									</ul>
								)}
								<figure className={styles.photo}>
									<img src={h.photos[1] || h.photos[0]} alt={`Mad Monkey ${h.name}`} loading="lazy" />
									<figcaption>Photo: Mad Monkey {h.name}</figcaption>
								</figure>
								<div className={styles.stay}>
									<div className={styles.stayHead}>
										<MarkerIcon name="bed" size={18} />
										<b>Where to stay in {h.name}</b>
									</div>
									<div className={styles.stayCard}>
										<img src={h.photos[0]} alt="" loading="lazy" />
										<div>
											<b>
												<Link to={hostelPath(s)}>Mad Monkey {h.name}</Link>
											</b>
											<small>{h.tag}</small>
											<ul>
												{h.events.slice(0, 3).map((e) => (
													<li key={e}>{e}</li>
												))}
											</ul>
										</div>
									</div>
									<a className={styles.stayBtn} href={mmUrl(s)} rel="noopener">
										Mad Monkey {h.name} ↗
									</a>
								</div>
							</section>
						);
					})}

					<h2 className={styles.h2} style={{ marginTop: 56 }}>
						More Mad Monkey routes
					</h2>
					<div className={styles.moreTrips}>
						{others.map((t) => (
							<TripCard key={t.id} trip={t} />
						))}
					</div>
				</article>

				<aside className={styles.side}>
					<div className={styles.sideMap}>
						<span className={cx(styles.pill, styles.sm)}>
							<Flag cc={ccs[0]} size={16} />
							{ccs.length > 1 ? `${ccs.length} COUNTRIES` : COUNTRIES[ccs[0]].toUpperCase()}
						</span>
						<div className={styles.sideMapView}>
							<LiveMap
								still
								id="mm-route-side"
								label={`Map of the stops of ${trip.name}`}
								hostels={sideHostels}
								route={slugs}
								fit={fit}
								interactive={false}
								dots={false}
							/>
						</div>
						<a href="#stop-1" className={styles.sideLink} onClick={jumpTo("stop-1")}>
							View the stops <span>→</span>
						</a>
					</div>
					<div className={styles.routeBox}>
						<span className={styles.tag}>THE ROUTE</span>
						{trip.stops.map(([s], i) => (
							<a key={s} href={`#stop-${i + 1}`} className={styles.rl} onClick={jumpTo(`stop-${i + 1}`)}>
								<img src={HOSTELS[s].photos[0]} alt="" />
								<span>
									<small>{dayLabel(ranges[i])}</small>
									<b>{HOSTELS[s].name}</b>
								</span>
							</a>
						))}
					</div>
					<div className={styles.cta}>
						<h3>Plan this route yourself?</h3>
						<p>Open the full route in Stippl: every stop on the map, the nights set, and room to change it with your crew.</p>
						<GetTripButton
							id="mm-get-trip-route-side"
							stops={stops}
							name={trip.name}
							source={trip.id}
							className={cx(shared.btn, shared.btnGreen, styles.ctaBtn)}
						>
							Get this trip <Arrow />
						</GetTripButton>
					</div>
				</aside>
			</div>
		</>
	);
};

export default RoutePage;
